import { UtilsService } from './../../../../../core/utils/utils.service';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Job } from '../../job.model';
import { ProjectService } from '../../../../project.service';
import { DbService } from '../../../../../core/db/db.service';
import { SettingsService } from '../../../../../settings/settings.service';
import { lastValueFrom } from 'rxjs';
import { ServiceGroup } from '../../../../../service-groups/service-group.model';
import { TranslateService } from '@ngx-translate/core';
import { JobDetailsComponent } from '../job-details.component';
import { ModalController } from '@ionic/angular';

@Component({
    selector: 'app-job-linkings',
    templateUrl: './job-linkings.component.html',
    styleUrls: ['./job-linkings.component.scss'],
})
export class JobLinkingsComponent implements OnChanges {
    @Input() linkedJobIds: Array<string> = [];
    @Input() changesAllowed = false;
    @Input() currentJobId: string;
    @Output() removeJobLinkRequested = new EventEmitter<{ removedJobLinkIndex: number; removedJobLinkId: string }>();

    linkedJobInfos: Array<{ job: Job; notice: string }> = []; // if linking field, contains all linked job infos (as the field value only contains ids)

    // helper
    loading = false;

    linkedJobModalVisible = false; // true if we are currently viewing a linked job in a modal
    linkedJobModalJobData = null; // holds job data of linked job in modal
    linkedJobModalServiceGroupData = null; // holds service group data of linked job in modal
    linkedJobModalProjectData = null; // holds project data of linked job in modal

    constructor(
        private projectSrv: ProjectService,
        private db: DbService,
        public utils: UtilsService,
        public settingsSrv: SettingsService,
        private translate: TranslateService,
        private modalCtrl: ModalController
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        this.loadLinkedJobs();
    }

    /**
     * Loads current linked jobs based on current field value
     */
    async loadLinkedJobs() {
        this.linkedJobInfos = []; // reset because we fully load all jobs
        if (Array.isArray(this.linkedJobIds)) {
            for (let jobId of this.linkedJobIds) {
                try {
                    let jobData = new Job(await lastValueFrom(this.db.getItemById(jobId)));
                    this.linkedJobInfos.push({
                        job: jobData,
                        notice: null,
                    });
                } catch (dbErr) {
                    this.linkedJobInfos.push({
                        job: null,
                        notice: this.translate.instant('projects.jobs.details.linking.data-not-found', { jobId: jobId }),
                    });
                }
            }
        }
    }

    /**
     * Checks if the user is allowed to view a linked job
     * @param itemId
     */
    showDisplayLinkedJobButton(itemId) {
        const visibleGroupIds = this.projectSrv.currentUserProjectSharingData.projectPermissions.visibleServiceGroups;
        if (
            this.settingsSrv.currentUserRights.canManageProjects ||
            visibleGroupIds.length == 0 ||
            visibleGroupIds.findIndex((vId) => vId == itemId) !== -1
        ) {
            return true;
        }

        return false;
    }

    /**
     * Triggered when the user wants to view a linked job
     */
    async onViewLinkedJob(job: Job) {
        // load sg from db
        try {
            let sgData = new ServiceGroup(await lastValueFrom(this.db.getItemById(job.serviceGroupId)));
            if (sgData.syncedServiceGroup && sgData.originalId != null) {
                await this.projectSrv.loadOriginalServiceGroupForServiceGroup(sgData);
            }
            this.linkedJobModalServiceGroupData = sgData;
            this.linkedJobModalProjectData = await lastValueFrom(this.db.getItemById(job.projectId));
            this.linkedJobModalJobData = job;

            if (!this.utils.isPhone) {
                this.linkedJobModalVisible = true;
                return;
            }

            await this.presentDetailedJobInModal();
        } catch (err) {
            this.linkedJobModalServiceGroupData = null;
            this.linkedJobModalProjectData = null;
            this.linkedJobModalJobData = null;
            this.linkedJobModalVisible = false;
        }
    }

    /**
     * Present detailed job modal for phone
     */
    async presentDetailedJobInModal(): Promise<void> {
        let jobModal = await this.modalCtrl.create({
            component: JobDetailsComponent,
            componentProps: {
                job: this.linkedJobModalJobData,
                project: this.linkedJobModalProjectData,
                serviceGroup: this.linkedJobModalServiceGroupData,
                subView: true,
                asDialog: true, // not really a dialog but activates the "dialog" ui version
                presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
            },
            animated: true,
            handle: false,
            backdropDismiss: true,
            backdropBreakpoint: 1,
            initialBreakpoint: 1,
            breakpoints: [0, 1],
            canDismiss: async (data?: any, role?: string) => {
                // prevent closing modal from swiping down -> this prevents a recent bug in ionic we have which cancels user activity
                return role !== 'gesture';
            },
        });
        await jobModal.present();

        const { data } = await jobModal.onWillDismiss();

        this.linkedJobModalServiceGroupData = null;
        this.linkedJobModalProjectData = null;
        this.linkedJobModalJobData = null;
        this.linkedJobModalVisible = false;

        if (data?.job != null) {
            this.onLinkedJobWasSaved({ job: data.job, closeJobModal: data?.closeJobModal });
        }
    }

    /**
     * Triggered when a linked job is saved
     * @param saveData
     */
    onLinkedJobWasSaved(saveData: { job: Job; closeJobModal: boolean }) {
        if (saveData.closeJobModal) {
            // reset and hide modal
            this.linkedJobModalServiceGroupData = null;
            this.linkedJobModalProjectData = null;
            this.linkedJobModalJobData = null;
            this.linkedJobModalVisible = false;

            // find job in our linked job data and refresh it (maybe job has changed)
            let jobIndex = this.linkedJobInfos.findIndex((x) => x.job._id === saveData.job._id);
            if (jobIndex > -1) {
                this.linkedJobInfos[jobIndex].job = saveData.job;
            }
        }
    }

    /**
     * Triggered when the user removes a linkedJob
     * @param index
     */
    onRemoveJobLink(index: number) {
        this.removeJobLinkRequested.emit({ removedJobLinkIndex: index, removedJobLinkId: this.linkedJobIds[index] });
    }
}
